import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  FormControl,
  Select,
  MenuItem,
  SelectChangeEvent,
  IconButton,
  Chip,
  Pagination,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import type { SxProps, Theme } from '@mui/material/styles';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { useParams, useSearchParams } from 'react-router-dom';
import { useBrands, Brand } from '../hooks/useBrands';
import { useImageComparisonData } from '../hooks/useImageComparisonData';
import { format } from 'date-fns';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { SectionCard, SectionHeader, SectionLoading, NestedPanel, LegendItem } from './ui/Section';
import { useChartStyles, ChartTooltipCard, CategoryTick } from './ui/chartStyles';

// Two series share the chart: mean engagement (top axis, brand plum) and
// post count (bottom axis, amber), each colored to match its own axis.
const COUNT_COLOR = '#E0912F';

const navButtonSx = (side: 'left' | 'right'): SxProps<Theme> => (theme) => ({
  position: 'absolute',
  [side]: 8,
  width: 36,
  height: 36,
  borderRadius: '50%',
  bgcolor: theme.palette.background.paper,
  color: theme.palette.text.primary,
  boxShadow: '0 2px 8px rgba(27, 21, 38, 0.18)',
  '&:hover': { bgcolor: theme.palette.background.paper, color: theme.palette.primary.main },
});

interface ImageFeaturesProps {
  title?: string;
  /** Which brand in the list to select first. */
  defaultBrandIndex?: number;
  startDate?: Date | null;
  endDate?: Date | null;
}

// Interface for the summary data from API
interface FeatureSummary {
  label: string;
  label_count: number;
  avg_engscore: number;
}

// Interface for image mapping data from API
interface ImageMapping {
  short_code: string;
  label: string;
  post_engscore: number;
  image_name: string;
}

// Interface for consolidated image data
interface ConsolidatedImage {
  image_name: string;
  short_code: string;
  labels: string[];
  post_engscore: number;
}

// Interface for chart data format
interface ChartData {
  name: string;
  engagementScore: number;
  labelCount: number;
}

const ImageFeatures: React.FC<ImageFeaturesProps> = ({ 
  title = "TOP FEATURES",
  defaultBrandIndex = 0,
  startDate = null,
  endDate = null
}) => {
  const { orgId: orgIdStr } = useParams<{ orgId: string }>();
  const orgId = parseInt(orgIdStr || '0', 10);
  const [searchParams] = useSearchParams();
  const { brands, loading: brandsLoading, error: brandsError } = useBrands(orgId);
  
  const [selectedBrand, setSelectedBrand] = useState<string>('');
  const [selectedLabel, setSelectedLabel] = useState<string | null>(null);
  const [currentImageIndex, setCurrentImageIndex] = useState<number>(0);
  
  // Pagination state
  const [page, setPage] = useState<number>(1);
  const [itemsPerPage] = useState<number>(10);

  // Sort method state
  const [sortMethod, setSortMethod] = useState<'engagement' | 'count'>('engagement');
  const cs = useChartStyles();
  const ENGAGEMENT_COLOR = useTheme().palette.primary.main;

  // Get minEngagementScore from URL params or default to 0
  const minEngagementScore = searchParams.get('minEngagementScore') 
    ? parseInt(searchParams.get('minEngagementScore')!, 10) 
    : 0;
  
  // Get minFeatureCount from URL params or default to 0
  const minFeatureCount = searchParams.get('minFeatureCount') 
    ? parseInt(searchParams.get('minFeatureCount')!, 10) 
    : 0;
    
  // Get keyword from URL params
  const keyword = searchParams.get('keyword') || '';

  // Pick the default brand when brands load (the comparison panel starts on
  // a different brand than the first one)
  useEffect(() => {
    if (brands && brands.length > 0 && !selectedBrand) {
      setSelectedBrand(brands[Math.min(defaultBrandIndex, brands.length - 1)].id);
    }
  }, [brands, selectedBrand, defaultBrandIndex]);

  // Log when date props change
  useEffect(() => {
    console.log(`ImageFeatures (${title}) - Props changed:`, {
      startDate: startDate ? format(startDate, 'yyyy-MM-dd') : null,
      endDate: endDate ? format(endDate, 'yyyy-MM-dd') : null,
      selectedBrand,
      minEngagementScore,
      minFeatureCount
    });
  }, [title, startDate, endDate, selectedBrand, minEngagementScore, minFeatureCount]);

  // Format dates for API call
  const formattedStartDate = startDate ? format(startDate, 'yyyy-MM-dd') : null;
  const formattedEndDate = endDate ? format(endDate, 'yyyy-MM-dd') : null;

  // Fetch image comparison data
  const { 
    data: imageComparisonData, 
    loading: dataLoading, 
    error: dataError 
  } = useImageComparisonData({
    brandId: selectedBrand,
    startDate: formattedStartDate,
    endDate: formattedEndDate,
    minEngagementScore,
    minFeatureCount,
    keyword
  });

  // Get all summary data, not just the paginated view
  const getAllSummaryData = (): FeatureSummary[] => {
    if (!imageComparisonData || !imageComparisonData.summary) {
      return [];
    }

    // Get the summary data and sort based on selected method
    const summaryData = [...imageComparisonData.summary] as FeatureSummary[];
    
    if (sortMethod === 'engagement') {
      summaryData.sort((a, b) => b.avg_engscore - a.avg_engscore);
    } else { // sort by count
      summaryData.sort((a, b) => b.label_count - a.label_count);
    }
    
    return summaryData;
  };

  // Process the data for the chart with pagination
  const processChartData = (): ChartData[] => {
    const summaryData = getAllSummaryData();
    if (summaryData.length === 0) {
      return [];
    }

    // Calculate start and end indices for the current page
    const startIndex = (page - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;

    // Take items for the current page and map to the chart format
    return summaryData.slice(startIndex, endIndex).map(item => ({
      name: item.label,
      engagementScore: Math.round(item.avg_engscore),
      labelCount: item.label_count
    }));
  };

  // Calculate the total number of pages
  const getTotalPages = (): number => {
    const summaryData = getAllSummaryData();
    return Math.ceil(summaryData.length / itemsPerPage);
  };

  // Handle page change
  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
    setSelectedLabel(null); // Reset selected label when changing pages
  };

  // Get chart data
  const chartData = processChartData();
  const totalPages = getTotalPages();

  // Reset image index when label selection changes
  useEffect(() => {
    setCurrentImageIndex(0);
  }, [selectedLabel]);

  // Reset pagination and label selection when brand changes
  useEffect(() => {
    setSelectedLabel(null);
    setCurrentImageIndex(0);
    setPage(1); // Reset to first page when brand changes
  }, [selectedBrand]);

  // Reset image index when data source changes (brand, dates, filters)
  useEffect(() => {
    setCurrentImageIndex(0);
    setPage(1); // Reset to first page when data criteria changes
  }, [startDate, endDate, minEngagementScore, minFeatureCount, keyword]);

  // Get relevant images based on the selected label, consolidating duplicates
  const getRelevantImages = (): ConsolidatedImage[] => {
    if (!imageComparisonData || !imageComparisonData.image_mapping) {
      return [];
    }

    const imageMapping = imageComparisonData.image_mapping as ImageMapping[];
    
    // First, get the image names we want to show based on filter criteria
    const relevantImageNames = new Set<string>();
    
    if (selectedLabel) {
      // If a label is selected, get images containing that label
      imageMapping
        .filter(img => img.label === selectedLabel)
        .forEach(img => relevantImageNames.add(img.image_name));
    } else {
      // When no label is selected, use all images containing labels from the complete dataset
      const allLabels = getAllSummaryData().map(item => item.label);
      imageMapping
        .filter(img => allLabels.includes(img.label))
        .forEach(img => relevantImageNames.add(img.image_name));
    }
    
    // Now consolidate ALL labels for these relevant images
    const imageMap: Record<string, ConsolidatedImage> = {};
    
    // First pass: create base entries for all relevant images
    imageMapping
      .filter(img => relevantImageNames.has(img.image_name))
      .forEach(img => {
        if (!imageMap[img.image_name]) {
          imageMap[img.image_name] = {
            image_name: img.image_name,
            short_code: img.short_code,
            labels: [],
            post_engscore: img.post_engscore
          };
        }
        
        // Track the highest engagement score
        if (img.post_engscore > imageMap[img.image_name].post_engscore) {
          imageMap[img.image_name].post_engscore = img.post_engscore;
        }
      });
    
    // Second pass: add ALL labels for these images
    imageMapping
      .filter(img => relevantImageNames.has(img.image_name))
      .forEach(img => {
        if (!imageMap[img.image_name].labels.includes(img.label)) {
          imageMap[img.image_name].labels.push(img.label);
        }
      });
    
    // Convert map to array
    return Object.values(imageMap);
  };

  // Get current image data
  const getCurrentImageData = (): ConsolidatedImage | null => {
    const relevantImages = getRelevantImages();
    if (relevantImages.length === 0) return null;
    
    // Ensure current index is valid
    const safeIndex = currentImageIndex % relevantImages.length;
    return relevantImages[safeIndex];
  };

  // Construct GCP image URL
  const getImageUrl = (imageName: string) => {
    return `https://storage.googleapis.com/inklantern-media-data/Instagram_url/${imageName}.jpeg`;
  };

  // Handle next/previous image navigation
  const handlePrevImage = () => {
    const relevantImages = getRelevantImages();
    if (relevantImages.length === 0) return;
    
    setCurrentImageIndex((prevIndex) => {
      if (prevIndex <= 0) {
        return relevantImages.length - 1;
      }
      return prevIndex - 1;
    });
  };

  const handleNextImage = () => {
    const relevantImages = getRelevantImages();
    if (relevantImages.length === 0) return;
    
    setCurrentImageIndex((prevIndex) => {
      return (prevIndex + 1) % relevantImages.length;
    });
  };

  // Handle label selection from chart
  const handleLabelClick = (label: string) => {
    setSelectedLabel(prevLabel => prevLabel === label ? null : label);
    setCurrentImageIndex(0);
  };

  // Log the API request/response for debugging
  useEffect(() => {
    if (selectedBrand && formattedStartDate && formattedEndDate) {
      console.log(`ImageFeatures (${title}) - API request:`, {
        brandId: selectedBrand,
        startDate: formattedStartDate,
        endDate: formattedEndDate,
        minEngagementScore,
        minFeatureCount,
        keyword
      });
    }
    
    if (imageComparisonData) {
      console.log(`ImageFeatures (${title}) - API response received with status:`, imageComparisonData.status || 'unknown');
      console.log(`ImageFeatures (${title}) - Chart data:`, chartData);
    }
  }, [selectedBrand, formattedStartDate, formattedEndDate, imageComparisonData, minEngagementScore, minFeatureCount, keyword, chartData, title]);
  
  const currentImage = getCurrentImageData();
  const relevantImages = getRelevantImages();

  const handleBrandChange = (event: SelectChangeEvent<string>) => {
    setSelectedBrand(event.target.value);
  };

  // Handle sort method change
  const handleSortMethodChange = (event: SelectChangeEvent<string>) => {
    setSortMethod(event.target.value as 'engagement' | 'count');
    setPage(1); // Reset to first page when changing sort
  };

  // Custom tooltip for the chart to show clickable labels
  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      return (
        <ChartTooltipCard
          title={payload[0].payload.name}
          rows={[
            { color: ENGAGEMENT_COLOR, label: 'Engagement score (mean)', value: payload[0].value },
            { color: COUNT_COLOR, label: 'Post count', value: payload[1]?.value },
          ]}
          footer="Click to see images with this feature"
        />
      );
    }
    return null;
  };

  // Show loading state
  if (brandsLoading) {
    return (
      <SectionCard>
        <SectionHeader title={title} />
        <SectionLoading height={360} />
      </SectionCard>
    );
  }

  // Show error state
  if (brandsError) {
    return (
      <SectionCard>
        <SectionHeader title={title} />
        <Typography color="error" sx={{ py: 5, textAlign: 'center' }}>Brands didn't load: {brandsError}</Typography>
      </SectionCard>
    );
  }

  return (
    <SectionCard>
      <SectionHeader
        title={title}
        actions={
          <>
            <FormControl variant="outlined" size="small" sx={{ minWidth: 220 }}>
              <Select
                value={selectedBrand}
                onChange={handleBrandChange}
                displayEmpty
                inputProps={{ 'aria-label': 'Brand' }}
                sx={{ height: 36, fontSize: '0.875rem', fontWeight: 600 }}
              >
                <MenuItem value="" disabled>Select brand</MenuItem>
                {brands.map((brand: Brand) => (
                  <MenuItem key={brand.id} value={brand.id}>
                    {brand.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <ToggleButtonGroup
              size="small"
              exclusive
              value={sortMethod}
              aria-label="Sort features by"
              onChange={(_e, next: 'engagement' | 'count' | null) => {
                if (next) {
                  setSortMethod(next);
                  setPage(1); // Reset to first page when changing sort
                }
              }}
            >
              <ToggleButton value="engagement">By engagement</ToggleButton>
              <ToggleButton value="count">By post count</ToggleButton>
            </ToggleButtonGroup>
          </>
        }
      />

      {dataLoading ? (
        <SectionLoading height={360} />
      ) : dataError ? (
        <Typography color="error" sx={{ py: 5, textAlign: 'center' }}>Image data didn't load: {dataError}</Typography>
      ) : (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1fr) minmax(0, 1.25fr)' }, gap: 2 }}>
          {/* Left side - Bar chart with pagination below */}
          <NestedPanel sx={{ display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1, minHeight: 28 }}>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <LegendItem color={ENGAGEMENT_COLOR} label="Engagement score (mean, top axis)" />
                <LegendItem color={COUNT_COLOR} label="Post count (bottom axis)" />
              </Box>
              {selectedLabel && (
                <Chip
                  size="small"
                  color="primary"
                  variant="outlined"
                  label={`Feature: ${selectedLabel}`}
                  onDelete={() => setSelectedLabel(null)}
                />
              )}
            </Box>

            <Box sx={{ height: 420, minHeight: 0 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  layout="vertical"
                  data={chartData}
                  margin={{ top: 4, right: 16, left: 0, bottom: 4 }}
                  barGap={2}
                  barCategoryGap="22%"
                  onClick={(data) => {
                    if (data && data.activeLabel) {
                      handleLabelClick(data.activeLabel);
                    }
                  }}
                >
                  <CartesianGrid stroke={cs.line} strokeDasharray="3 4" horizontal={false} />
                  <XAxis
                    xAxisId={0}
                    type="number"
                    orientation="top"
                    dataKey="engagementScore"
                    name="Engagement Score"
                    domain={[0, 'dataMax + 5']}
                    tick={{ fontSize: 12, fill: ENGAGEMENT_COLOR }}
                    tickLine={false}
                    axisLine={{ stroke: cs.line }}
                  />
                  <XAxis
                    xAxisId={1}
                    type="number"
                    orientation='bottom'
                    dataKey="labelCount"
                    name="Post Count"
                    allowDecimals={false}
                    domain={[0, (dataMax: number) => Math.ceil(dataMax * 1.1)]}
                    tick={{ fontSize: 12, fill: COUNT_COLOR }}
                    tickLine={false}
                    axisLine={{ stroke: cs.line }}
                  />
                  <YAxis
                    {...cs.categoryAxis}
                    dataKey="name"
                    type="category"
                    width={124}
                    interval={0}
                    tick={<CategoryTick fill={cs.categoryAxis.tick.fill} maxLength={17} />}
                    style={{ cursor: 'pointer' }}
                  />
                  <Tooltip content={<CustomTooltip />} cursor={cs.cursor} />
                  <Bar dataKey="engagementScore" name="Engagement Score" fill={ENGAGEMENT_COLOR} xAxisId={0} radius={[0, 3, 3, 0]} maxBarSize={14} isAnimationActive={false}>
                    {chartData.map((entry, index) => (
                      <Cell
                        key={`cell-eng-${index}`}
                        fill={ENGAGEMENT_COLOR}
                        fillOpacity={selectedLabel && entry.name !== selectedLabel ? 0.35 : 1}
                        cursor="pointer"
                      />
                    ))}
                  </Bar>
                  <Bar dataKey="labelCount" name="Post Count" fill={COUNT_COLOR} xAxisId={1} radius={[0, 3, 3, 0]} maxBarSize={14} isAnimationActive={false}>
                    {chartData.map((entry, index) => (
                      <Cell
                        key={`cell-count-${index}`}
                        fill={COUNT_COLOR}
                        fillOpacity={selectedLabel && entry.name !== selectedLabel ? 0.35 : 1}
                        cursor="pointer"
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Box>

            {totalPages > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={handlePageChange}
                  color="primary"
                  size="small"
                />
              </Box>
            )}
          </NestedPanel>

          {/* Right side - Image display */}
          <NestedPanel sx={{ display: 'flex', flexDirection: 'column', minHeight: 480 }}>
            {relevantImages.length > 0 && currentImage ? (
              <>
                <Box
                  sx={(theme) => ({
                    position: 'relative',
                    flex: '1 1 auto',
                    height: 380,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '8px',
                    overflow: 'hidden',
                    bgcolor: theme.palette.mode === 'dark' ? '#0F0C14' : '#ECE8F0',
                  })}
                >
                  <img
                    src={getImageUrl(currentImage.image_name)}
                    alt={`Image with features: ${currentImage.labels.join(', ')}`}
                    style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                  />

                  {/* Navigation buttons */}
                  {relevantImages.length > 1 && (
                    <>
                      <IconButton
                        aria-label="Previous image"
                        onClick={handlePrevImage}
                        sx={navButtonSx('left')}
                      >
                        <ArrowBackIosNewIcon fontSize="small" />
                      </IconButton>

                      <IconButton
                        aria-label="Next image"
                        onClick={handleNextImage}
                        sx={navButtonSx('right')}
                      >
                        <ArrowForwardIosIcon fontSize="small" />
                      </IconButton>
                    </>
                  )}
                </Box>

                <Box sx={{ mt: 2, flex: '0 0 auto' }}>
                  <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 2 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      Image {currentImageIndex + 1} of {relevantImages.length}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                      Engagement score <Box component="strong" sx={{ color: 'text.primary' }}>{currentImage.post_engscore.toFixed(2)}</Box>
                    </Typography>
                  </Box>
                  <Box sx={{ mt: 1.25, display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                    {currentImage.labels.map((label, idx) => (
                      <Chip
                        key={idx}
                        label={label}
                        size="small"
                        color={label === selectedLabel ? 'primary' : 'default'}
                        variant={label === selectedLabel ? 'filled' : 'outlined'}
                        onClick={() => handleLabelClick(label)}
                      />
                    ))}
                  </Box>
                </Box>
              </>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  {selectedLabel
                    ? `No images have the feature "${selectedLabel}".`
                    : 'Pick a feature in the chart to see its images.'
                  }
                </Typography>
              </Box>
            )}
          </NestedPanel>
        </Box>
      )}
    </SectionCard>
  );
};

export default ImageFeatures;